<script setup lang="ts">
import { isRunningInNativeApp } from '~/utils/browserInfo';

type AppStore = {
  id: 'google-play' | 'apple-store';
  href: string;
  src: string;
  alt: string;
  width: number;
  height: number;
};

const stores = computed<AppStore[]>(() => [
  {
    id: 'google-play',
    href: 'https://play.google.com/store/apps/details?id=br.bet.apostatudo.twa',
    src: getAssetsUrlForCDN('/assets/seals/google-play.svg'),
    alt: 'Google Play',
    width: 167,
    height: 50,
  },
  {
    id: 'apple-store',
    href: 'https://apps.apple.com/br/app/apostatudo/id6773755738',
    src: getAssetsUrlForCDN('/assets/seals/apple-store.svg'),
    alt: 'App Store',
    width: 167,
    height: 50,
  },
]);

/**
 * Dentro do app nativo (WebView) esconde os badges de "baixar o app": o
 * usuario ja esta no app, e a referencia ao Google Play viola a App Store
 * guideline 2.3.10. Renderiza no SSR/primeiro paint e esconde apos o mount
 * — `isRunningInNativeApp` so e confiavel no client (evita mismatch de
 * hidratacao). O rodapé no navegador continua exibindo os badges.
 */
const isInNativeApp = ref(false);

onMounted(() => {
  isInNativeApp.value = isRunningInNativeApp();
});
</script>

<template>
  <div
    v-if="!isInNativeApp"
    class="footer-v2-app-stores"
  >
    <a
      v-for="store in stores"
      :key="store.id"
      :aria-label="store.alt"
      class="footer-v2-app-stores__btn"
      :href="store.href"
      rel="noopener noreferrer"
      target="_blank"
    >
      <img
        :alt="store.alt"
        decoding="async"
        :height="store.height"
        loading="lazy"
        :src="store.src"
        :width="store.width"
      />
    </a>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-app-stores {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;

  /* Desktop (≥1024px) — empilhados verticalmente na coluna esquerda.
     Wrapper ocupa 281px (largura da coluna esquerda) para que o
     `align-items: flex-start` produza alinhamento à esquerda
     consistente com Logo/Social acima. Gap 5px é o espaço
     entre os dois badges (Google Play e App Store). */
  @media (min-width: 1024px) {
    width: 281px;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  &__btn {
    /* O SVG do badge já contém a borda branca, padding interno
       e textos ("Disponível no Google Play" / "Salve o app na App Store").
       O wrapper apenas controla o tamanho-alvo (166.5px de largura)
       e mantém o link clicável. */
    display: flex;
    width: 166.5px;
    justify-content: center;
    align-items: center;
    text-decoration: none;
    box-sizing: border-box;

    /* Desktop: largura levemente menor (157px) conforme Figma. */
    @media (min-width: 1024px) {
      width: 157px;
    }

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
